import _ from 'lodash';
import { CLIENT_STATE_KEYS, } from '../client-state-type';
import { AxisTypes, ChartType } from '../types/prism';
import { generateAxes, REQUIRED_AXIS_UPGRADE_PROPERTIES } from './chart-axis';
import { CHART_CLIENT_STATE_LATEST_VERSION } from './chart-client-state-init';
const reusableChartProperties = [
    CLIENT_STATE_KEYS.gridLines,
    CLIENT_STATE_KEYS.allLabels,
    CLIENT_STATE_KEYS.dataSize,
    CLIENT_STATE_KEYS.showStackedLabels,
    CLIENT_STATE_KEYS.responsiveLayoutPreference,
    CLIENT_STATE_KEYS.showLinearRegressionLine,
    CLIENT_STATE_KEYS.chartSpecific,
];
function setAxisProperties(vizModel) {
    var _a, _b;
    const clientState = vizModel.vizProps;
    const yAxisColumns = ((_a = vizModel.axisConfig) === null || _a === void 0 ? void 0 : _a.yAxisColumns) || [];
    const isYAxisLinkingOptionNotAllowed = [
        ChartType.StackedColumn,
        ChartType.StackedBar,
        ChartType.StackedArea,
        ChartType.Candlestick,
    ].includes(vizModel.chartType);
    const yAxes = generateAxes(yAxisColumns, AxisTypes.Y, {
        isLinkColumns: isYAxisLinkingOptionNotAllowed,
        additionalProperties: REQUIRED_AXIS_UPGRADE_PROPERTIES,
    });
    const xAxisColumns = (_b = vizModel.axisConfig) === null || _b === void 0 ? void 0 : _b.xAxisColumns;
    const xAxis = generateAxes(xAxisColumns, AxisTypes.X, {
        additionalProperties: REQUIRED_AXIS_UPGRADE_PROPERTIES,
    });
    return [].concat(yAxes, xAxis);
}
function canReuseAxisProperties(vizProp, axisConfig) {
    const axisProperties = vizProp.axisProperties;
    const yAxisColumns = [];
    const xAxisColumns = [];
    _.forEach(axisProperties, axis => {
        if (axis.properties.axisType === AxisTypes.Y) {
            Array.prototype.push.apply(yAxisColumns, axis.properties.linkedColumns || []);
        }
        else {
            Array.prototype.push.apply(xAxisColumns, axis.properties.linkedColumns || []);
        }
    });
    const yXor = _.xor(yAxisColumns, axisConfig.yAxisColumns || []);
    const xXor = _.xor(xAxisColumns, axisConfig.xAxisColumns || []);
    return !yXor.length && !xXor.length;
}
function getAllColumnInAxisConfig(axisConfig) {
    var _a, _b, _c, _d;
    let colIds = [];
    if (((_a = axisConfig.xAxisColumns) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        colIds = [...colIds, ...axisConfig.xAxisColumns];
    }
    if (((_b = axisConfig.yAxisColumns) === null || _b === void 0 ? void 0 : _b.length) > 0) {
        colIds = [...colIds, ...axisConfig.yAxisColumns];
    }
    if (((_c = axisConfig.legendColumns) === null || _c === void 0 ? void 0 : _c.length) > 0) {
        colIds = [...colIds, ...axisConfig.legendColumns];
    }
    if (((_d = axisConfig.sortColumns) === null || _d === void 0 ? void 0 : _d.length) > 0) {
        colIds = [...colIds, ...axisConfig.sortColumns];
    }
    if (axisConfig.categoryColumns) {
        colIds.push(axisConfig.categoryColumns);
    }
    if (axisConfig.radialColumn) {
        colIds.push(axisConfig.radialColumn);
    }
    return colIds;
}
function reuseClientState(vizModel) {
    const { axisConfig, columns } = vizModel;
    const vizProps = _.cloneDeep(vizModel.vizProps);
    let reuseChartProperties = false;
    if (_.isNil(vizProps) ||
        _.isNil(vizProps.version) ||
        vizProps.version !== CHART_CLIENT_STATE_LATEST_VERSION) {
        throw new Error('incompatible viz props received while trying to reuse axis');
    }
    let columnProperties = vizProps.columnProperties || [];
    const colIds = columns.map(obj => obj.id);
    if (!canReuseAxisProperties(vizProps, axisConfig)) {
        vizProps.axisProperties = setAxisProperties(vizModel);
        reuseChartProperties = true;
    }
    if (columnProperties && columnProperties.length > 0) {
        const visualisedColIds = Object.values(axisConfig)
            .reduce((acc, colList) => acc.concat(colList), [])
            .filter(val => val);
        columnProperties = columnProperties.filter(colMap => colIds.includes(colMap.columnId));
        const newlyVisualisedColumns = _.difference(visualisedColIds, columnProperties.map(colMap => colMap.columnId));
        vizProps.columnProperties = columnProperties;
        _.forEach(newlyVisualisedColumns, colId => {
            var _a, _b;
            vizProps.columnProperties.push({
                columnId: colId,
                columnProperty: {
                    __typename: 'ChartVizColumnProperties',
                    conditionalFormatting: null,
                    dataLabels: (_b = (_a = vizProps.chartProperties) === null || _a === void 0 ? void 0 : _a.allLabels) !== null && _b !== void 0 ? _b : null,
                    excludeNullValues: null,
                    handleMissingValues: null,
                    customProps: null,
                    kpiColumnProperties: null,
                },
            });
        });
        if (newlyVisualisedColumns.length > 0) {
            reuseChartProperties = true;
        }
    }
    if (reuseChartProperties) {
        _.forIn(vizProps.chartProperties, (value, key) => {
            if (key !== '__typename' &&
                !reusableChartProperties.includes(key)) {
                vizProps.chartProperties[key] = null;
            }
        });
    }
    return vizProps;
}
const testHooks = {
    canReuseAxisProperties,
};
export { reuseClientState, setAxisProperties, testHooks };
//# sourceMappingURL=chart-reuse-axis.js.map